import React from "react";
import { motion } from "framer-motion";
import { PROGRAMS } from "@/lib/site";
import {
  Heart,
  Accessibility,
  GraduationCap,
  Briefcase,
  Users,
  HeartPulse,
  ArrowUpRight,
} from "lucide-react";

const ICONS = {
  Heart,
  Accessibility,
  GraduationCap,
  Briefcase,
  Users,
  HeartPulse,
};

function ProgramCard({ p, span, dark }) {
  const Icon = ICONS[p.icon] || Heart;
  return (
    <motion.article
      data-testid={`program-card-${p.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className={`relative ${span} rounded-[24px] overflow-hidden ring-1 ring-brand-border hover-lift group ${
        dark ? "bg-brand-navy text-white" : "bg-white"
      }`}
    >
      <div className="relative h-56 sm:h-64 lg:h-72 overflow-hidden">
        <img
          src={p.image}
          alt={p.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 text-brand-forest text-[11px] uppercase tracking-[0.16em] font-semibold">
          <Icon className="h-3.5 w-3.5" />
          {p.short}
        </div>
      </div>

      <div className="p-6 lg:p-8">
        <h3
          className={`font-serif text-2xl lg:text-3xl leading-tight ${
            dark ? "text-white" : "text-brand-ink"
          }`}
        >
          {p.title}
        </h3>
        <p
          className={`mt-3 text-[15px] leading-relaxed ${
            dark ? "text-white/80" : "text-brand-muted"
          }`}
        >
          {p.summary}
        </p>

        <ul className="mt-5 space-y-2">
          {p.bullets.map((b) => (
            <li
              key={b}
              className={`text-sm flex items-start gap-2 ${
                dark ? "text-white/80" : "text-brand-ink/80"
              }`}
            >
              <span
                className={`mt-1.5 h-1.5 w-1.5 rounded-full ${
                  dark ? "bg-brand-gold" : "bg-brand-forest"
                }`}
              />
              {b}
            </li>
          ))}
        </ul>

        <a
          href="#contact"
          data-testid={`program-link-${p.slug}`}
          className={`mt-7 inline-flex items-center gap-1.5 text-sm font-semibold underline-link ${
            dark ? "text-brand-gold" : "text-brand-forest"
          }`}
        >
          Learn more
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </motion.article>
  );
}

export default function Programs() {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative py-20 lg:py-28 bg-brand-sand/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              What we do
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink tracking-tight leading-[1.05]">
              Six pillars.{" "}
              <span className="italic text-brand-forest">One promise.</span>
            </h2>
          </div>
          <p className="text-brand-muted text-lg max-w-md">
            Every program is designed to meet a mother where she is — and to
            stand beside her until her family is thriving.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <div className="md:col-span-7">
            <ProgramCard p={PROGRAMS[0]} span="" />
          </div>
          <div className="md:col-span-5">
            <ProgramCard p={PROGRAMS[1]} span="" dark />
          </div>

          <div className="md:col-span-4">
            <ProgramCard p={PROGRAMS[2]} span="" />
          </div>
          <div className="md:col-span-4">
            <ProgramCard p={PROGRAMS[3]} span="" />
          </div>
          <div className="md:col-span-4">
            <ProgramCard p={PROGRAMS[4]} span="" />
          </div>

          <div className="md:col-span-12">
            <ProgramCard p={PROGRAMS[5]} span="" />
          </div>
        </div>
      </div>
    </section>
  );
}
